import React, { useCallback } from 'react';
import P from 'prop-types';
// eslint-disable-next-line lodash/import-scope
import property from 'lodash/property';
import { Box, Title, Text } from '@nerdwallet/currency';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import Image from '@nerdwallet/react-image';
import classNames from 'classnames';
import BackgroundColor from '../background-color/background-color';
import getTextColor from '../../lib/text-color-config';
import parseMarkdownLinks from '../../lib/parse-md-links';
import styles from './branded-hero.module.less';
import {
  boolCheckboxSpec,
  group,
  numberInputSpec,
  stringInputSpec,
  stringSelectSpec,
} from '../../lib/editing-configs';

const getTextColorStyle = (textColorOptions, propertyName = 'color') => {
  const { textColor: color } = getTextColor(textColorOptions);
  return { [propertyName]: color };
};

const linkParser = (content) => parseMarkdownLinks(content, false);

const htmlOutputParser = (content, enableHtml = false) => {
  if (enableHtml) {
    // eslint-disable-next-line react/no-danger
    return <span dangerouslySetInnerHTML={{ __html: content }} />;
  }
  return linkParser(content);
};

const BrandedHero = ({
  bgOptions,
  title,
  titleSize,
  titleColorOptions,
  titleChronicle,
  subtitle,
  subtitleSize,
  subtitleColorOptions,
  enableHtmlInSubtitle,
  heroImage,
  heroLogo,
  brandedHeroImageDesktop,
  brandedHeroImageMobile,
  brandedHeroImageTablet,
  backgroundHeight,
  backgroundHeightMobile,
  titleMaxWidthDesktop,
  titleMaxWidthMobile,
  subtitleMaxWidthDesktop,
  subtitleMaxWidthMobile,
  size,
}) => {
  const getBackgroundImageSrcMap = useCallback(
    (bgSize) => {
      const type = {
        lg: brandedHeroImageDesktop,
        md: brandedHeroImageTablet,
        sm: brandedHeroImageMobile,
      };
      if (bgSize !== 'lg' && !type[bgSize]) {
        return type.lg;
      }
      return type[bgSize];
    },
    [brandedHeroImageDesktop, brandedHeroImageMobile, brandedHeroImageTablet]
  );

  const backgroundSize =
    size === 'lg'
      ? `100% ${backgroundHeight || 50}%`
      : `100% ${backgroundHeightMobile || 20}%`;

  const getTitleMaxSize =
    size === 'lg' ? titleMaxWidthDesktop : titleMaxWidthMobile;
  const getSubtitleMaxSize =
    size === 'lg' ? subtitleMaxWidthDesktop : subtitleMaxWidthMobile;

  return (
    <BackgroundColor bgOptions={bgOptions}>
      {(backgroundColor) => (
        <div
          className={styles.container}
          style={{
            backgroundColor,
            backgroundSize,
            backgroundImage: `url(${getBackgroundImageSrcMap(size)})`,
          }}
        >
          {heroLogo && (
            <Image
              className={styles.heroLogo}
              naturalHeight={90}
              naturalWidth={211}
              backgroundColor="transparent"
              src={heroLogo}
              alt="Hero Logo"
            />
          )}
          <Title
            style={{
              ...getTextColorStyle(titleColorOptions),
              maxWidth: getTitleMaxSize,
            }}
            className={classNames(styles.heroTitle, [
              { [styles.titleChronicle]: titleChronicle },
            ])}
            size={titleSize}
          >
            {title}
          </Title>
          <Box
            style={{
              ...getTextColorStyle(subtitleColorOptions),
              maxWidth: getSubtitleMaxSize,
            }}
            className={styles.heroSubtitle}
          >
            <Text size={subtitleSize}>
              {htmlOutputParser(subtitle, enableHtmlInSubtitle)}
            </Text>
          </Box>
          {heroImage && (
            <Image
              className={styles.heroImage}
              naturalHeight={400}
              naturalWidth={1114}
              backgroundColor="transparent"
              src={heroImage}
              alt="Hero Image"
            />
          )}
        </div>
      )}
    </BackgroundColor>
  );
};

BrandedHero.displayName = 'BrandedHero';

BrandedHero.defaultProps = {
  title: '',
  titleSize: 'small',
  subtitle: '',
  subtitleSize: 'default',
  heroImage: '',
  heroLogo: '',
  brandedHeroImageDesktop: '',
  brandedHeroImageMobile: '',
  brandedHeroImageTablet: '',
  backgroundHeight: 50,
  backgroundHeightMobile: 20,
  titleChronicle: false,
  enableHtmlInSubtitle: false,
  titleMaxWidthDesktop: '',
  titleMaxWidthMobile: '',
  subtitleMaxWidthDesktop: '',
  subtitleMaxWidthMobile: '',
  bgOptions: {
    hexColor: '',
    backgroundColor: '',
  },
  titleColorOptions: {
    hexColor: '',
    textColor: '',
  },
  subtitleColorOptions: {
    hexColor: '',
    textColor: '',
  },
  children: null,
};

BrandedHero.propTypes = {
  title: P.string,
  titleSize: P.oneOf(['default', 'small']),
  subtitle: P.string,
  subtitleSize: P.oneOf(['default', 'large', 'small', 'x-small']),
  heroImage: P.string,
  heroLogo: P.string,
  brandedHeroImageDesktop: P.string,
  brandedHeroImageMobile: P.string,
  brandedHeroImageTablet: P.string,
  backgroundHeight: P.number,
  backgroundHeightMobile: P.number,
  size: P.string,
  titleChronicle: P.bool,
  enableHtmlInSubtitle: P.bool,
  titleMaxWidthDesktop: P.number,
  titleMaxWidthMobile: P.number,
  subtitleMaxWidthDesktop: P.number,
  subtitleMaxWidthMobile: P.number,
  bgOptions: P.shape({
    backgroundColor: P.string,
  }),
  titleColorOptions: P.shape({
    hexColor: P.string,
    textColor: P.string,
  }),
  subtitleColorOptions: P.shape({
    hexColor: P.string,
    textColor: P.string,
  }),
  children: P.node,
};

BrandedHero.getEditingConfig = (componentDocument) => [
  group('Image', [
    stringInputSpec('heroLogo', 'Hero Logo URL'),
    stringInputSpec('heroImage', 'Hero Image'),
  ]),
  group('Title', [
    stringInputSpec('title', 'Title'),
    stringSelectSpec(
      'titleSize',
      'Title Size',
      [
        { label: 'Small', value: 'small' },
        { label: 'Large', value: 'default' },
      ],
      {
        help: 'Defaults to small',
      }
    ),
    boolCheckboxSpec('titleChronicle', 'Title in Chronicle?'),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'titleColorOptions',
      'Title Color'
    ),
    stringInputSpec('titleMaxWidthDesktop', 'Title Max Width Desktop'),
    stringInputSpec('titleMaxWidthMobile', 'Title Max Width Mobile'),
  ]),
  group('Subtitle', [
    stringInputSpec('subtitle', 'Subtitle'),
    stringSelectSpec(
      `subtitleSize`,
      'Subtitle Size',
      [
        { label: 'Default', value: 'default' },
        { label: 'Large', value: 'large' },
        { label: 'Small', value: 'small' },
        { label: 'X-Small', value: 'x-small' },
      ],
      {
        help: 'Defaults to small',
      }
    ),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'subtitleColorOptions',
      'Subtitle Color'
    ),
    boolCheckboxSpec('enableHtmlInSubtitle', 'Allow HTML in Subtitle?', {
      help: 'WARNING: We strongly advise against making use of this feature unless you really need it.',
    }),
    stringInputSpec('subtitleMaxWidthDesktop', 'Subtitle Max Width Desktop'),
    stringInputSpec('subtitleMaxWidthMobile', 'Subtitle Max Width Mobile'),
  ]),
  group('Background', [
    stringInputSpec(
      'brandedHeroImageDesktop',
      'Background Image URL (Desktop)'
    ),
    stringInputSpec('brandedHeroImageTablet', 'Background Image URL (Tablet)'),
    stringInputSpec('brandedHeroImageMobile', 'Background Image URL (Mobile)'),
    numberInputSpec('backgroundHeight', 'Background Image Height Percentage', {
      help: 'Defaults to "50"',
    }),
    numberInputSpec(
      'backgroundHeightMobile',
      'Background Image Height Percentage in Mobile',
      {
        help: 'Defaults to "20"',
      }
    ),
    ...BackgroundColor.getEditingConfig(componentDocument),
  ]),
];

export default connect(
  createStructuredSelector({
    size: property('mediaQuery'),
  })
)(BrandedHero);
